import currency from "currency.js";
import moment from "moment";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DataContext } from "../../context/DataContext";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_STYLE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import {
  ITodayWithdrawal,
  useTodayWithdrawals,
} from "../../hooks/api/admin/useTodayWithdrawals";
import { Spinner } from "../shared/Spinner";
import { chainIdToExplorer } from "../../utils/chain";
import { shortenString } from "../../utils/strings";

// Compact section showing every finalized L1 withdrawal in the current
// kill-switch window (env-anchored: mainnet 08:00 UTC, testnet 04:15 UTC).
// Rendered unconditionally above the manual-accounts table so ops can
// see what's flowing out regardless of whether the kill switch is
// tripped — handy for spotting trends before the threshold is hit.
//
// Collapsed by default so the page doesn't feel crowded; ops opens it
// when investigating or scanning the day's outflows.
type SortKey = "account" | "amount" | "asset" | "finalized";
type SortDir = "asc" | "desc";

export function TodayWithdrawals() {
  const { data, isLoading, error } = useTodayWithdrawals();
  const { records } = useContext(DataContext);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [sortKey, setSortKey] = useState<SortKey>("finalized");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState<boolean>(false);

  const updateScrollIndicator = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setHasMoreBelow(false);
      return;
    }
    const remaining = el.scrollHeight - (el.scrollTop + el.clientHeight);
    setHasMoreBelow(remaining > 8);
  }, []);

  const scrollDown = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      top: Math.max(el.clientHeight - 40, 80),
      behavior: "smooth",
    });
  }, []);

  const totalUsd = useMemo(() => {
    if (!data?.withdrawals?.length) return 0;
    return data.withdrawals.reduce((acc, w) => acc + (w.amount_human || 0), 0);
  }, [data]);

  const count = data?.count ?? 0;
  const windowStart = data?.window_start_iso
    ? moment(data.window_start_iso).format("DD MMM HH:mm [UTC]")
    : "—";

  const sortedWithdrawals = useMemo(() => {
    if (!data?.withdrawals?.length) return [];
    const dir = sortDir === "asc" ? 1 : -1;
    const labelFor = (w: ITodayWithdrawal) =>
      (records[w.Account] || w.Account).toLowerCase();
    const assetFor = (w: ITodayWithdrawal) =>
      (w.CollateralName || w.Collateral || "").toLowerCase();
    return [...data.withdrawals].sort((a, b) => {
      switch (sortKey) {
        case "account": {
          const av = labelFor(a);
          const bv = labelFor(b);
          if (av === bv) return 0;
          return av < bv ? -dir : dir;
        }
        case "amount":
          return ((a.amount_human || 0) - (b.amount_human || 0)) * dir;
        case "asset": {
          const av = assetFor(a);
          const bv = assetFor(b);
          if (av === bv) return 0;
          return av < bv ? -dir : dir;
        }
        case "finalized":
        default:
          return (
            ((a.FinalizedTimestamp || 0) - (b.FinalizedTimestamp || 0)) * dir
          );
      }
    });
  }, [data, sortKey, sortDir, records]);

  // Re-evaluate whether there's hidden content below whenever the expanded
  // state, the row data, or the sort flips. The scroll handler keeps it
  // live as the user scrolls.
  useEffect(() => {
    if (!expanded) {
      setHasMoreBelow(false);
      return;
    }
    updateScrollIndicator();
  }, [expanded, sortedWithdrawals, updateScrollIndicator]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "account" || key === "asset" ? "asc" : "desc");
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  const headerCellStyle = (align: "left" | "right" = "left") => ({
    padding: SPACING.two,
    textAlign: align,
    cursor: "pointer",
    userSelect: "none" as const,
    whiteSpace: "nowrap" as const,
  });

  return (
    <div
      style={{
        borderBottom: `1px solid ${BORDER_COLORS.one}`,
        background: BACKGROUND_COLORS.componentSolid,
        display: "flex",
        flexDirection: "column",
        minHeight: 40,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: SPACING.two,
          padding: `0 ${SPACING.three}px`,
          height: 40,
          background: "transparent",
          border: "none",
          borderBottom: expanded ? `1px solid ${BORDER_COLORS.one}` : "none",
          color: TEXT_COLORS.one,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            ...FONT_STYLE.label.two,
            color: TEXT_COLORS.two,
            whiteSpace: "nowrap",
          }}
        >
          Today&apos;s Withdrawals
        </span>
        <span
          style={{
            ...FONT_STYLE.label.one,
            color: TEXT_COLORS.one,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {isLoading && !data ? "…" : `${count} finalized`}
        </span>
        <span
          style={{
            ...FONT_STYLE.label.two,
            color: TEXT_COLORS.two,
            whiteSpace: "nowrap",
          }}
        >
          {isLoading && !data
            ? ""
            : `≈ ${currency(totalUsd, { precision: 0 }).format()}`}
        </span>
        <span
          style={{
            ...FONT_STYLE.label.two,
            color: TEXT_COLORS.three,
            whiteSpace: "nowrap",
          }}
        >
          since {windowStart}
        </span>

        <div style={{ flex: 1 }} />

        {error ? (
          <span
            style={{
              ...FONT_STYLE.label.two,
              color: COLORS.red.one,
              whiteSpace: "nowrap",
            }}
          >
            Failed to load
          </span>
        ) : null}
        <span
          style={{
            ...FONT_STYLE.label.two,
            color: TEXT_COLORS.two,
            whiteSpace: "nowrap",
          }}
        >
          {expanded ? "▾ Hide" : "▸ Show"}
        </span>
      </button>

      {expanded ? (
        <div style={{ position: "relative" }}>
          <div
            ref={scrollRef}
            onScroll={updateScrollIndicator}
            style={{
              overflow: "auto",
              maxHeight: 320,
            }}
          >
            {isLoading && !data ? (
              <div
                style={{
                display: "flex",
                justifyContent: "center",
                padding: SPACING.four,
              }}
              >
                <Spinner />
              </div>
          ) : null}
            {!isLoading && count === 0 ? (
              <div
                style={{
                padding: SPACING.three,
                color: TEXT_COLORS.two,
                ...FONT_STYLE.label.two,
              }}
              >
                No finalized withdrawals in the current window.
              </div>
          ) : null}
            {count > 0 ? (
              <table
                style={{
                width: "100%",
                borderCollapse: "collapse",
                ...FONT_STYLE.label.two,
              }}
              >
                <thead>
                  <tr
                    style={{
                    color: TEXT_COLORS.two,
                    textAlign: "left",
                  }}
                  >
                    <th
                      style={headerCellStyle("left")}
                      onClick={() => handleSort("account")}
                    >
                      Account{sortIndicator("account")}
                    </th>
                    <th
                      style={headerCellStyle("right")}
                      onClick={() => handleSort("amount")}
                    >
                      Amount{sortIndicator("amount")}
                    </th>
                    <th
                      style={headerCellStyle("left")}
                      onClick={() => handleSort("asset")}
                    >
                      Asset{sortIndicator("asset")}
                    </th>
                    <th
                      style={headerCellStyle("left")}
                      onClick={() => handleSort("finalized")}
                    >
                      Finalized{sortIndicator("finalized")}
                    </th>
                    <th style={{ padding: SPACING.two }}>L1 Tx</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedWithdrawals.map((w: ITodayWithdrawal) => {
                  const name = records[w.Account];
                  const explorer = chainIdToExplorer(w.ChainID);
                  return (
                    <tr
                      key={`${w.L1TxHash}-${w.FinalizedTimestamp}`}
                      style={{
                        borderTop: `1px solid ${BORDER_COLORS.one}`,
                      }}
                    >
                      <td style={{ padding: SPACING.two }}>
                        <a
                          href={`/account/${w.Account}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: name ? COLORS.blue.one : TEXT_COLORS.one,
                            textDecoration: "none",
                          }}
                        >
                          {name || shortenString(w.Account, 6, 4)}
                        </a>
                      </td>
                      <td
                        style={{
                          padding: SPACING.two,
                          textAlign: "right",
                          color: TEXT_COLORS.one,
                        }}
                      >
                        {currency(w.amount_human, { precision: 2 }).format({
                          symbol: "",
                        })}
                      </td>
                      <td
                        style={{ padding: SPACING.two, color: TEXT_COLORS.one }}
                      >
                        {w.CollateralName || w.Collateral}
                      </td>
                      <td
                        style={{ padding: SPACING.two, color: TEXT_COLORS.two }}
                      >
                        {w.finalized_at
                          ? moment(w.finalized_at).format(
                              "DD MMM HH:mm:ss [UTC]"
                            )
                          : "—"}
                      </td>
                      <td style={{ padding: SPACING.two }}>
                        {w.L1TxHash ? (
                          <a
                            href={`${explorer}tx/${w.L1TxHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: COLORS.blue.one }}
                          >
                            {shortenString(w.L1TxHash, 6, 4)}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
          ) : null}
          </div>
          {hasMoreBelow ? (
            <button
              type="button"
              onClick={scrollDown}
              title="Scroll down for more"
              style={{
                position: "absolute",
                right: SPACING.three,
                bottom: SPACING.two,
                width: 28,
                height: 28,
                borderRadius: 14,
                border: `1px solid ${BORDER_COLORS.one}`,
                background: BACKGROUND_COLORS.componentSolid,
                color: TEXT_COLORS.one,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                ...FONT_STYLE.label.one,
              }}
            >
              ▼
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
